import React from 'react';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '@/components/ui/table';

const MAX_PREVIEW_ROWS = 20;

interface PasteGridPreviewProps {
    parsedData: string[][];
}

/**
 * A parsed clipboard grid rendered the way it will land: first row as the
 * header, the rest as data rows, capped so a 400-row paste cannot push the
 * confirm button off screen.
 *
 * Shared by the two surfaces that show a paste before applying it — the preview
 * that gates a paste already in flight (`PastePreviewModal`) and the modal that
 * waits for one (`PasteInstrumentModal`) — so the row cap and the two
 * degenerate shapes below are decided once.
 */
export const PasteGridPreview: React.FC<PasteGridPreviewProps> = ({ parsedData }) => {
    if (parsedData.length === 0) return null;

    const headerRow = parsedData[0];
    const bodyRows = parsedData.slice(1);
    const totalBodyRows = bodyRows.length;
    const visibleBodyRows = bodyRows.slice(0, MAX_PREVIEW_ROWS);
    const hiddenRowCount = totalBodyRows - visibleBodyRows.length;

    return (
        <div className="max-h-80 overflow-hidden overflow-y-auto rounded-lg border">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/50">
                        {headerRow.map((cell, colIdx) => (
                            <TableHead
                                key={colIdx}
                                className="whitespace-nowrap text-xs font-semibold"
                            >
                                {cell || '\u00A0'}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {visibleBodyRows.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                            {headerRow.map((_, colIdx) => (
                                <TableCell key={colIdx} className="whitespace-nowrap text-xs">
                                    {row[colIdx] ?? ''}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {hiddenRowCount > 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={headerRow.length}
                                className="py-3 text-center text-xs italic text-muted-foreground"
                            >
                                ...e mais {hiddenRowCount} linha
                                {hiddenRowCount > 1 ? 's' : ''}
                            </TableCell>
                        </TableRow>
                    )}
                    {totalBodyRows === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={headerRow.length}
                                className="py-3 text-center text-xs italic text-muted-foreground"
                            >
                                Somente cabeçalho (sem linhas de dados)
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};
